import type { OnboardingTourController } from "@gfazioli/mantine-onboarding-tour";
import { createContext } from "react";

import type { HelpTourStep } from "src/help/helpTours";

/** One chapter of the current page's tour and how many of its steps are available now. */
export type HelpTourChapter = { chapter: string; steps: number };

/**
 * Exposes the current page's help identity, panel state and onboarding tour controls.
 */
export type HelpContextValue = {
    /** Whether a help provider is mounted; only the modern layout provides help. */
    available: boolean;
    /** The route pattern that keys help topics and tours, such as `/trials/:trialId/details`. */
    routeKey: string;
    /** The route whose help topic this page shows; shared by pages that render the same page. */
    helpRouteKey: string;
    /** Whether the viewport is sm or wider, where help is an aside rather than a drawer. */
    desktop: boolean;
    opened: boolean;
    open: () => void;
    close: () => void;
    toggle: () => void;
    /** Tour steps for the route that the user is permitted to see at the page context. */
    tourSteps: readonly HelpTourStep[];
    /**
     * Requests a tour over the permitted steps, of one chapter or of every chapter, whose targets are
     * displayed or can be revealed.
     */
    startTour: (chapter?: string) => void;
    /** The chapters a tour could visit now, in tour order, with how many steps each has. */
    getTourChapters: () => HelpTourChapter[];
    /** The running tour, or undefined when no tour is in progress. */
    tourController?: OnboardingTourController;
};

/**
 * Provides help state, defaulting to an unavailable, closed state without a tour.
 *
 * Postconditions: Without a provider every action does nothing, so shared components that render
 * help targets remain usable in the legacy layout.
 */
const HelpContext = createContext<HelpContextValue>({
    available: false,
    routeKey: "/",
    helpRouteKey: "/",
    desktop: true,
    opened: false,
    open: () => undefined,
    close: () => undefined,
    toggle: () => undefined,
    tourSteps: [],
    startTour: () => undefined,
    getTourChapters: () => []
});

export default HelpContext;
